import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User as UserType } from "@supabase/supabase-js";
import { useOptimizedProfileData } from "@/hooks/profile/useOptimizedProfileData";
import { useNotifications } from "@/hooks/useNotifications";
import { useUserVerification } from "@/components/profile/hooks/useUserVerification";
import { AccountMenuBody } from "../account-menu/AccountMenuBody";
import type { AppRole } from "@/types/supabase";

interface EnhancedUserMenuProps {
  user: UserType;
  userRole: string | null;
  onSignOut: () => void;
  getDashboardLink: () => string;
}

export function EnhancedUserMenu({
  user,
  userRole,
  onSignOut,
}: EnhancedUserMenuProps) {
  const { profile } = useOptimizedProfileData(user.id);
  const { unreadCount } = useNotifications();
  const { isVerified } = useUserVerification(user);
  const [open, setOpen] = useState(false);

  const displayName =
    profile?.display_name ||
    profile?.name ||
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    user.email?.split("@")[0] ||
    "User";
  const avatarUrl = profile?.avatar_url || user.user_metadata?.avatar_url;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2.5 pl-[5px] pr-3 py-[5px] bg-white border border-[#1E1B4B]/[0.14] rounded-[2px]"
        >
          <img
            src={avatarUrl || "/placeholder.svg"}
            alt={displayName}
            className="h-[30px] w-[30px] flex-shrink-0 rounded-full object-cover bg-[#EFEFF7]"
          />
          <span className="font-archivo font-semibold text-sm text-[#1E1B4B]">
            {displayName.split(" ")[0]}
          </span>
          <ChevronDown size={15} className="text-[#6E6B8A]" strokeWidth={1.9} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-[268px] p-0 bg-white border border-[#1E1B4B]/[0.12] rounded-[3px] shadow-[0_20px_48px_rgba(30,27,75,0.16)] overflow-hidden"
      >
        <AccountMenuBody
          variant="light"
          displayName={displayName}
          email={user.email}
          role={userRole as AppRole | null}
          isVerified={isVerified}
          unreadNotifications={unreadCount}
          onNavigate={() => setOpen(false)}
          onSignOut={onSignOut}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
